import React, { useEffect, useState } from 'react';

import { useTranslation } from "core/hooks/useTranslation";
import { useLocation, useNavigate } from "react-router-dom";

import { Col, Form, Row, Spin } from 'antd';

import SubTabFormDashboardLayout from "components/layouts/tab/subtab/form";
import Input from 'components/common/input';
import Icon from "components/common/icon";

import useUnsavedChangesConfirmation from 'hooks/useUnsavedChangesConfirmation';

import { isFormChanged } from "utils/form";
import { deleteQueryStringParamsToUrl } from 'utils/queryString';

import { NAME_REGEX, LICENSE_REGEX } from 'constants/regex.constants';
import { PERMISSION_RESOURCE, PERMISSION_ACTION } from 'core/constants/permission';
import { UNSAVED_FORM_PAGE_TYPE } from 'constants/common.constants';


import usePermissions from 'core/hooks/usePermission';
import brandService from 'core/services/apiServices/brandService';
import { FORM_FIELD_NAMES } from './constants.';
import showSuccess from 'core/helpers/common/showSuccess';

const GeneralInfoComponent = ({
    onTabChange
}) => {
    const { t } = useTranslation();

    const permissionUtils = usePermissions();

    const { search, hash, pathname } = useLocation();
    const navigate = useNavigate();

    const [isFormTouched, setIsFormTouched] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [generalInfo, setGeneralInfo] = useState({})
    
    const navigateWithConfirmation = useUnsavedChangesConfirmation({
        cb: navigate,
        subscribe: [UNSAVED_FORM_PAGE_TYPE.SUB_TAB]
    })

    const brandId = (new URLSearchParams(search)).get("brandId");

    const [ formInstance ] = Form.useForm();
    const { setFieldsValue } = formInstance;

    const hasModifyPermission = permissionUtils.has( PERMISSION_RESOURCE.PROJECT_BRANDS, PERMISSION_ACTION.MODIFY );

    
    useEffect(() => {
        setIsLoading(true);
        brandService.getGeneralInfo({
            brandId
        }).then( value => {
            setFieldsValue({
                [FORM_FIELD_NAMES.NAME]: value.name,
                [FORM_FIELD_NAMES.LICENSE_ID]: value.licenseId
            });
            setGeneralInfo(value);
        }).finally(() => {
            setIsLoading(false);
        })
    }, [])

    const handleSave = () => {
        formInstance.submit();
    }

    const handleFormSubmit = formData => {
        setIsSaving(true);
        const d = {
            id: brandId,
            name: formData[FORM_FIELD_NAMES.NAME],
            licenseId: formData[FORM_FIELD_NAMES.LICENSE_ID]
        }

        brandService.saveGeneralInfo(d)
            .then(( {value, message} ) => {
                setGeneralInfo(value);
                setIsFormTouched(false);
                showSuccess(message);
            }).finally(() => {
                setIsSaving(false);
            })
    }

    const handleFormValuesChange = (_, values) => {
        const initialValues = {
            [FORM_FIELD_NAMES.NAME]: generalInfo.name,
            [FORM_FIELD_NAMES.LICENSE_ID]: generalInfo.licenseId
        }

        const formValues = {
            [FORM_FIELD_NAMES.NAME]: values[FORM_FIELD_NAMES.NAME],
            [FORM_FIELD_NAMES.LICENSE_ID]: values[FORM_FIELD_NAMES.LICENSE_ID]
        }

        setIsFormTouched(isFormChanged(initialValues, formValues))
    }

    useEffect(() => {
        onTabChange(isFormTouched);
    }, [isFormTouched])

    return (
        <SubTabFormDashboardLayout
            buttons={[
                {
                    type: "primary",
                    onClick: handleSave,
                    text: t("backoffice.common.save"),
                    enabled: hasModifyPermission,
                    loading: isSaving,
                    disabled: !isFormTouched
                },
                {
                    type: "secondary",
                    onClick: () => {
                        const url = deleteQueryStringParamsToUrl(pathname, search, hash, ["brandId", "brandName"] );
                        navigateWithConfirmation(url, { replace: true } )
                    },
                    text: (
                        <span className='rt--back-button'>
                            <Icon name="left" size={16} />
                            <span>
                                {t("backoffice.common.back")}
                            </span>
                        </span>
                    ),
                    enabled: true,
                    className: "rt--button-secondary",
                    placement: "left",
                },
            ]}
        >
            <Spin spinning={isLoading} wrapperClassName="rt--form-spin">
                <Form
                    form={formInstance}
                    requiredMark={false}
                    layout="vertical"
                    onFinish={handleFormSubmit}
                    onValuesChange={handleFormValuesChange}
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12} lg={6}>
                            <Form.Item
                                label={`${t('backoffice.projects.name')} *`}
                                name="name"
                                rules={[
                                    { required: true, whitespace: true, message: t('backoffice.validation.fieldRequired') },
                                    { max: 30, message: t('backoffice.validation.fieldInvalid') },
                                    { min: 2, message: t('backoffice.validation.fieldInvalid') },
                                    { pattern: NAME_REGEX, message: t('backoffice.validation.fieldInvalid') }
                                ]}
                                className={"rt--general-form-item" + (!hasModifyPermission ? " rt--form-item-disabled" : "" )}
                                data-placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.name')}`}
                                validateFirst
                            >
                                <Input
                                    placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.name')}`}
                                    maxLength={30}
                                    disabled={!hasModifyPermission}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12} xl={6}>
                            <Form.Item
                                label={t('backoffice.projects.licenseId')}
                                name="licenseId"
                                rules={[
                                    { max: 11, message: t('backoffice.validation.fieldInvalid') },
                                    { pattern: LICENSE_REGEX, message: t('backoffice.validation.fieldInvalid') }
                                ]}
                                validateFirst
                                className={"rt--general-form-item" + (!hasModifyPermission ? " rt--form-item-disabled" : "" )}
                                data-placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.licenseId')}`}
                            >
                                <Input
                                    placeholder={`${t('backoffice.common.enter')} ${t('backoffice.projects.licenseId')}`}
                                    maxLength={11}
                                    disabled={!hasModifyPermission}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </SubTabFormDashboardLayout>
    );
}

export default GeneralInfoComponent;